import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  WeightTrackingAnalyticsEvent,
  WeightTrackingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import {
  ModalTestID,
  NavTestID,
  WeightTrackingTestID,
} from "@/constants/TestID";
import { router, useLocalSearchParams } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import { useGetWeightObservationsQuery } from "@/store/api/vitals";
import {
  useAppUserProxyPatientsInTreatmentSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import WeightTrackingHistory from "@/ui/components/vitals/history/WeightTrackingHistory";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import UserTabSelector from "@/ui/core/UserTabSelector";
import ModalLayout from "@/ui/layout/ModalLayout";
import { joinTestIDs } from "@/utils/string.util";
import { getTabSelectorOptions } from "@/utils/user.util";

const Modal = () => {
  const { track, trackPageView, trackUserSelectorEvent } = useAnalytics();

  useEffect(() => {
    trackPageView(WeightTrackingAnalyticsPageView.History);
  }, [trackPageView]);

  const appUser = useAppUserSelector();
  const { externalId: appUserId, isPatient: isAppUserPatient } = appUser;
  const proxyPatients = useAppUserProxyPatientsInTreatmentSelector();
  // if the parent context (the Home tab - weight tracking) has selected a tab
  // before entering this modal, then propagate that selected tab here
  // NOTE: we do NOT propagate the selected user tab here back to the parent context
  const { parentSelectedUserId } = useLocalSearchParams<{
    parentSelectedUserId: string;
  }>();
  const initialUserId = parentSelectedUserId || "";
  const [selectedUserId, setSelectedUserId] = useState<string>(initialUserId);
  const patientExternalId = selectedUserId;

  const userTabs =
    getTabSelectorOptions({
      appUserCount: 0,
      appUserId,
      isAppUserPatient,
      patients: proxyPatients,
      shouldIncludeSelf: false,
    }) || [];

  const onUserChange = (userId: string) => {
    setSelectedUserId(userId);
    trackUserSelectorEvent({
      selectedUser: userId,
      source: ModalPath.WeightHistory,
    });
  };

  const onModalDismiss = () => {
    track(WeightTrackingAnalyticsEvent.DismissWeightHistoryPressed, {
      [AnalyticsFieldName.Component]: ModalPath.WeightHistory,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: ModalPath.WeightHistory,
    });
    router.dismiss();
  };

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [shouldIncrementPage, setShouldIncrementPage] =
    useState<boolean>(false);
  const {
    data: queryData,
    isFetching,
    isLoading,
  } = useGetWeightObservationsQuery({
    pageNumber,
    patientExternalId,
  });
  const data = queryData?.data || [];
  // calculate the maximum expected page number, to not infinitely increment the page number
  const maxPageNumber =
    isNil(queryData) || isNil(queryData.count) || isNil(queryData.pageSize)
      ? 1
      : Math.ceil(queryData.count / queryData.pageSize);

  useEffect(() => {
    // do not increment the page number until the previous page fetch is DONE loading
    if (isLoading || isFetching || !shouldIncrementPage) {
      // otherwise, doing so results in RTK terminating the previous fetch early
      // (as it effectively won't be used in any render), meaning a page is lost
      // when scrolling happens, this can happen repeatedly very quickly, resulting in almost ALL pages lost
      return;
    }
    const nextPageNum =
      pageNumber + 1 > maxPageNumber ? maxPageNumber : pageNumber + 1;
    setPageNumber(nextPageNum);
    setShouldIncrementPage(false);
  }, [isLoading, isFetching, pageNumber, maxPageNumber, shouldIncrementPage]);

  return (
    <ModalLayout
      isListView
      renderHeader={() => {
        return (
          <ModalTitleHeader
            actionComponents={
              <UserTabSelector
                accessibilityLabel="User Tab Selector"
                appUserId={appUserId}
                initialUserId={initialUserId}
                onUserChange={onUserChange}
                tabs={userTabs}
                testID={joinTestIDs(
                  ModalTestID.TitleHeader,
                  NavTestID.TabSelector,
                )}
              />
            }
            onDismiss={onModalDismiss}
            testID={joinTestIDs(
              ModalTestID.TitleHeader,
              WeightTrackingTestID.History,
            )}
            title="Weight history"
          />
        );
      }}
    >
      <WeightTrackingHistory
        data={data}
        onEndReached={() => {
          setShouldIncrementPage(true);
        }}
        // an API call fetches 60 items at a time, 12 are shown per screen, resulting in 5 screens of content
        // we set the threshold here to 4 screens worth of content to ensure
        // we have a full api call's worth of extra content to scroll through without delay
        onEndReachedThreshold={4}
      />
    </ModalLayout>
  );
};

export default Modal;
